import React, { useCallback, useEffect, useState } from "react";

import { useParams, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import Loader from "../../../Components/Loader";
import { GroupService } from "../../../DataService/GroupService";
import { AdminIcon } from "../../../Icons/AdminIcon";
import { GroupDto, UpdateGroupDto } from "../../../Types/GroupServiceTypes";
import { EditGroupModal } from "./EditGroupModal";
import { Icon } from "../../../Icons/Icon";
import { Button } from "../../../Components/Button";
import MembersList from "./MembersList";
import { DeleteGroupConfirmationModal } from "./DeleteGroupModal";
import { AddUserToGroupModal } from "./AddUserToGroupModal";
import '../AdminGroups.css'
const GroupDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [group, setGroup] = useState<GroupDto | null>(null);
  const [loading, setLoading] = useState(true);

  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const g = await GroupService.GetGroupById(id!);
    if (!g) {
      toast.warning("Gruppen kunde inte hittas");
      navigate("/admin/groups");
    } else {
      setGroup(g);
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    if (id) load();
  }, [id, load]);

  const handleUpdate = async (payload: UpdateGroupDto) => {
    if (!group) return;
    await GroupService.UpdateGroup(group.id, payload);
    await load();
    setShowEdit(false);
  };

  const handleDelete = async () => {
    if (!group) return;
    await GroupService.DeleteGroup(group.id);
    setShowDelete(false);
    navigate("/admin/groups");
  };

  const handleAddUser = async (email: string) => {
    if (!group) return;
    await GroupService.AddUserToGroup(group.id, email);
    await load();
    setShowAddUser(false);
  };

  return (
    <div className="admin-page-shell admin-groups-page-shell">
      <header className="admin-page-header admin-groups-title-container">
        <div>
          <button type="button" onClick={() => navigate("/admin/groups")} className="admin-groups-back-button">
            <Icon.ArrowLeft />
            <span>Tillbaka</span>
          </button>
          <h1 className="admin-page-title admin-groups-title-text">{group?.name ?? "Grupp"}</h1>
          <p className="admin-page-subtitle admin-groups-title-paragraph">{group?.description}</p>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <Button size="sm" className="submit-button" onClick={() => setShowEdit(true)} text={<><AdminIcon.EditPencil width={16} height={16} fill="white" /> Redigera</>} />
          <Button size="sm" className="submit-button" onClick={() => setShowAddUser(true)} text={<><AdminIcon.PlusSign width={16} height={16} fill="white" /> Lägg till medlem</>} />
          <Button size="sm" className="warning-button" onClick={() => setShowDelete(true)} text={<><AdminIcon.Trashcan width={16} height={16} fill="white" /> Ta bort</>} />
        </div>
      </header>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 24 }}>
            <Loader loaderSize="LARGE" bgColor="hsl(var(--primary))" />
          </div>
        ) : group ? (
          <div style={{ marginTop: 16 }}>
            <MembersList members={group.users} onRefresh={load} groupId={group.id} />
          </div>
        ) : null}

      {showEdit && group && (
        <EditGroupModal group={{ id: group.id, name: group.name, description: group.description }} isSaving={false}
          onSave={async (payload) => handleUpdate(payload)} onClose={() => setShowEdit(false)} />
      )}

      {showDelete && group && (
        <DeleteGroupConfirmationModal groupName={group.name} isDeleting={false}
          onDelete={async () => handleDelete()} onClose={() => setShowDelete(false)} />
      )}

      {showAddUser && group && (
        <AddUserToGroupModal groupName={group.name} isSaving={false}
          onAdd={async (email) => handleAddUser(email)} onClose={() => setShowAddUser(false)} />
      )}
    </div>
  );
};

export default GroupDetailsPage;
